import { Component, computed, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, combineLatest, EMPTY, from, Observable, of } from "rxjs";
import { catchError, filter, map, shareReplay, switchMap, take, tap } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { MainDataModels } from "src/app/models/maindata-models";
import { DatumFormatPipe } from "src/app/pipes/datum-format.pipe";
import { CalendarService } from "src/app/services/calendar.service";
import { HelperService } from "src/app/services/helper.service";
import { MainDataService } from "src/app/services/main-data.service";
import { MessageBoxService } from "src/app/services/message-box.service";
import { UserAuthenticationService } from "src/app/services/user-authentication.service";
import { AufgabeEintragComponent } from "./aufgabe-eintrag/aufgabe-eintrag.component";
import { TimeRange } from "./calendar/calendar.component";
import { NotizEintragComponent } from "./notiz-eintrag/notiz-eintrag.component";
import { TerminEintragComponent } from "./termin-eintrag/termin-eintrag.component";

const NO_APPOINTMENT = "Kein Termin ausgewählt.";
const DIALOG_WIDTH = "800px";
const REFRESH_ROUTE = "/pendenzen/kalender";

const SUCCESS_TITLE_KEY = "MAIN_APP_PENDENZEN.messageTitle.SUCCESS";
const ERROR_TITLE_KEY = "MAIN_APP_PENDENZEN.messageTitle.ERROR";
const APPOINTMENT_SAVED = "MAIN_APP_PENDENZEN.APPOINTMENT_SAVED.text";
const APPOINTMENT_UPDATED = "MAIN_APP_PENDENZEN.APPOINTMENT_UPDATED.text";
const APPOINTMENT_SAVE_ERROR = "MAIN_APP_PENDENZEN.ERROR_DIALOG.APPOINTMENT_SAVE_ERROR.text";
const APPOINTMENT_UPDATE_ERROR = "MAIN_APP_PENDENZEN.ERROR_DIALOG.APPOINTMENT_UPDATE_ERROR.text";
const TASK_SAVED = "MAIN_APP_PENDENZEN.TASK_SAVED.text";
const TASK_SAVE_ERROR = "MAIN_APP_PENDENZEN.ERROR_DIALOG.TASK_SAVE_ERROR.text";
const NOTE_SAVED = "MAIN_APP_PENDENZEN.NOTE_SAVED.text";
const NOTE_SAVE_ERROR = "MAIN_APP_PENDENZEN.ERROR_DIALOG.NOTE_SAVE_ERROR.text";

@Component({
    selector: "app-pendenzen",
    templateUrl: "./pendenzen.component.html",
    styleUrls: ["./pendenzen.component.scss"],
})
export class PendenzenComponent {
    private readonly svcMainData = inject(MainDataService);
    private readonly calendarService = inject(CalendarService);
    private readonly auth = inject(UserAuthenticationService);
    private readonly helper = inject(HelperService);
    private readonly messageBox = inject(MessageBoxService);
    private readonly datum = inject(DatumFormatPipe);
    private readonly dialog = inject(MatDialog);
    private readonly destroyRef = inject(DestroyRef);

    readonly fields = { text: "MitarbeiterBez", value: "UserName" };
    readonly mitarbeiterAuswahl = "Mitarbeiterauswahl";

    private readonly employees$ = this.calendarService.getEmployees().pipe(shareReplay({ bufferSize: 1, refCount: true }));
    readonly mitarbeiterDataSource = toSignal(this.employees$.pipe(map((employees) => this.toDataSource(employees))), { initialValue: [] as object[] });

    readonly selectedMitarbeiter = toSignal(combineLatest([this.employees$, this.calendarService.selectedEmployees$]).pipe(map(([, selected]) => selected)), {
        initialValue: [] as string[],
    });

    private readonly selectedAppointment$ = new BehaviorSubject<MainDataModels.Termin | undefined>(undefined);
    readonly selectedAppointment = toSignal(this.selectedAppointment$, { initialValue: undefined });
    readonly currPendenzExists = computed(() => !!this.selectedAppointment());

    readonly currentAppointment = toSignal(this.selectedAppointment$.pipe(switchMap((termin) => (termin ? this.describeAppointment(termin) : of(NO_APPOINTMENT)))), {
        initialValue: NO_APPOINTMENT,
    });

    private readonly selectedTimeRange$ = new BehaviorSubject<TimeRange | undefined>(undefined);

    constructor() {
        // Wurde der Browser aktualisiert?
        if (this.auth.getCurrentAuthenticatedUser() == undefined) {
            this.helper.setPageRefreshURL(REFRESH_ROUTE);
        }
    }

    onRemoved(args: any): void {
        const selected = this.selectedMitarbeiter().filter((userName) => userName !== args.itemData.UserName);
        this.calendarService.setSelectedEmployees(selected);
    }

    onSelect(args: any): void {
        const selected = [...this.selectedMitarbeiter(), args.itemData.UserName];
        this.calendarService.setSelectedEmployees(selected);
    }

    onNewTermin(): void {
        this.selectedTimeRange$
            .pipe(
                take(1),
                switchMap((range) => this.dialog.open(TerminEintragComponent, { disableClose: true, width: DIALOG_WIDTH, data: this.newTermin(range) }).afterClosed()),
                filter((result): result is MainDataModels.TerminMitBenutzer => !!result),
                map((result) => this.withGroupId(result)),
                switchMap((result) => this.saveAppointment(result, APPOINTMENT_SAVED, APPOINTMENT_SAVE_ERROR)),
                catchError((err) =>
                    of(err).pipe(
                        tap((error) => this.logError("onNewTermin", error)),
                        switchMap(() => this.recover()),
                    ),
                ),
            )
            .subscribe();
    }

    onNewAufgabe(): void {
        this.selectedTimeRange$
            .pipe(
                take(1),
                switchMap((range) => this.dialog.open(AufgabeEintragComponent, { disableClose: true, width: DIALOG_WIDTH, data: this.newAufgabe(range) }).afterClosed()),
                filter((result): result is MainDataModels.AufgabeMitBenutzer => !!result),
                switchMap((result) => this.saveAufgabe(result)),
                catchError((err) =>
                    of(err).pipe(
                        tap((error) => this.logError("onNewAufgabe", error)),
                        switchMap(() => this.recover()),
                    ),
                ),
            )
            .subscribe();
    }

    onNewNote(): void {
        this.dialog
            .open(NotizEintragComponent, { disableClose: true, width: DIALOG_WIDTH, data: this.newNote() })
            .afterClosed()
            .pipe(
                take(1),
                filter((result): result is MainDataModels.NotizMitBenutzer => !!result),
                switchMap((result) => this.saveNotiz(result)),
                catchError((err) =>
                    of(err).pipe(
                        tap((error) => this.logError("onNewNote", error)),
                        switchMap(() => this.recover()),
                    ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    showAppointmentDetails(): void {
        const termin = this.selectedAppointment();
        if (!termin) {
            return;
        }
        this.calendarService
            .loadAppointmentWithUsers(termin.id)
            .pipe(
                take(1),
                switchMap((loaded) => this.dialog.open(TerminEintragComponent, { disableClose: true, width: DIALOG_WIDTH, data: loaded }).afterClosed()),
                filter((result): result is MainDataModels.TerminMitBenutzer => !!result),
                switchMap((result) => this.saveAppointment(result, APPOINTMENT_UPDATED, APPOINTMENT_UPDATE_ERROR)),
                catchError((err) =>
                    of(err).pipe(
                        tap((error) => this.logError("showAppointmentDetails", error)),
                        switchMap(() => this.recover()),
                    ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    delSelectedItem(): void {
        const termin = this.selectedAppointment();
        if (!termin) {
            return;
        }
        from(this.messageBox.askYesNoQuestion("MAIN_APP_PENDENZEN.terminLöschen", "MAIN_APP_PENDENZEN.delete"))
            .pipe(
                take(1),
                filter((confirmed) => confirmed),
                switchMap(() => this.calendarService.deleteAppointment(termin.id)),
                catchError((err) =>
                    of(err).pipe(
                        tap((error) => this.logError("delSelectedItem", error)),
                        switchMap(() => this.recover()),
                    ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    onTimeRangeSelected(range?: TimeRange): void {
        this.selectedTimeRange$.next(range);
    }

    reloadAppointment(termin?: MainDataModels.Termin): void {
        this.selectedAppointment$.next(termin);
    }

    private saveAppointment(termin: MainDataModels.TerminMitBenutzer, successKey: string, errorKey: string): Observable<unknown> {
        return this.calendarService.saveAppointment(termin).pipe(
            tap(() => this.messageBox.showSuccessByLanguageKey(successKey, SUCCESS_TITLE_KEY)),
            catchError((err) =>
                of(err).pipe(
                    tap(() => this.messageBox.showErrorByLanguageKey(errorKey, ERROR_TITLE_KEY)),
                    tap((error) => this.logError("saveAppointment", error)),
                    switchMap(() => this.recover()),
                ),
            ),
        );
    }

    private saveAufgabe(aufgabe: MainDataModels.AufgabeMitBenutzer): Observable<unknown> {
        return this.svcMainData.saveAufgabe(aufgabe).pipe(
            tap(() => this.messageBox.showSuccessByLanguageKey(TASK_SAVED, SUCCESS_TITLE_KEY)),
            catchError((err) =>
                of(err).pipe(
                    tap(() => this.messageBox.showErrorByLanguageKey(TASK_SAVE_ERROR, ERROR_TITLE_KEY)),
                    tap((error) => this.logError("saveAufgabe", error)),
                    switchMap(() => this.recover()),
                ),
            ),
        );
    }

    private saveNotiz(notiz: MainDataModels.NotizMitBenutzer): Observable<unknown> {
        return this.svcMainData.saveNotiz(notiz).pipe(
            tap(() => this.messageBox.showSuccessByLanguageKey(NOTE_SAVED, SUCCESS_TITLE_KEY)),
            tap(() => this.svcMainData.refreshNoteCount()),
            catchError((err) =>
                of(err).pipe(
                    tap(() => this.messageBox.showErrorByLanguageKey(NOTE_SAVE_ERROR, ERROR_TITLE_KEY)),
                    tap((error) => this.logError("saveNotiz", error)),
                    switchMap(() => this.recover()),
                ),
            ),
        );
    }

    // Wenn keine Zelle markiert ist, darf kein Termin-Zeitraum vorbelegt werden.
    private newTermin(range?: TimeRange): MainDataModels.TerminMitBenutzer {
        const user = this.auth.getCurrentAuthenticatedUser()!;
        return {
            termin: {
                id: uuidv4(),
                objectID: "",
                adressID: undefined,
                description: "",
                username: user.loginname,
                location: "",
                private: 0,
                reminder: 1,
                reminderNumber: 15,
                notes: "",
                busyStatus: 2,
                startDate: range && this.datum.removeTimeFromDate(range.start),
                startTime: range && this.datum.transformOnlyTime(range.start),
                endDate: range && this.datum.removeTimeFromDate(range.end),
                endTime: range && this.datum.transformOnlyTime(range.end),
            },
            mitarbeiter: [],
            terminKategorien: [],
            istNeuerTermin: true,
            nurDiesenTerminBearbeiten: false,
        };
    }

    // Bei markierter Zelle deren Startdatum nehmen, sonst das heutige Datum.
    private newAufgabe(range?: TimeRange): MainDataModels.AufgabeMitBenutzer {
        const user = this.auth.getCurrentAuthenticatedUser()!;
        const startdate = range ? this.datum.removeTimeFromDate(range.start) : new Date(this.datum.transform(new Date(), "yyyy-MM-ddT") + "00:00:00");
        return {
            aufgabe: {
                taskUID: uuidv4(),
                objectID: "",
                adressID: undefined,
                responsible: user.loginname,
                description: "",
                importance: 1,
                done: false,
                percentDone: 0,
                status: 0,
                private: 0,
                accepted: 1,
                wasRead: 1,
                reminder: 0,
                notes: "",
                startdate,
                dueDate: startdate,
            },
            mitarbeiter: [],
            aufgabeKategorien: [],
            istNeueAufgabe: true,
        };
    }

    private newNote(): MainDataModels.NotizMitBenutzer {
        const user = this.auth.getCurrentAuthenticatedUser()!;
        return {
            notiz: {
                noteID: uuidv4(),
                objectID: "",
                objDesc: "",
                adressID: undefined,
                contactID: undefined,
                responsible: user.loginname,
                created: new Date(),
                createdBy: "",
                modified: new Date(),
                modifiedBy: "",
                notes: "",
                description: "",
                done: 0,
                private: false,
                wasRead: 0,
            },
            mitarbeiter: [],
            notizKategorien: [],
            istNeueNotiz: true,
        };
    }

    private withGroupId(termin: MainDataModels.TerminMitBenutzer): MainDataModels.TerminMitBenutzer {
        if (termin.mitarbeiter.length > 1) {
            termin.termin.groupID = uuidv4();
        }
        return termin;
    }

    private describeAppointment(termin: MainDataModels.Termin): Observable<string> {
        const description = termin.description;
        if (!termin.adressID) {
            return of(description);
        }
        return from(this.svcMainData.getTerminKundenAdresseZeile(termin.adressID)).pipe(
            map((customer) => (customer ? `${description} Kunde: ${customer}` : description)),
            catchError((err) =>
                of(err).pipe(
                    tap((error) => this.logError("describeAppointment", error)),
                    map(() => description),
                ),
            ),
        );
    }

    private toDataSource(employees: MainDataModels.Mitarbeiter[]): object[] {
        return employees
            .filter((employee) => employee.active != 0)
            .map((employee) => ({ MitarbeiterBez: `${employee.firstname} ${employee.lastname}`, UserName: employee.loginname }));
    }

    /** Central error logger for this component. */
    private logError(context: string, error: unknown): void {
        console.error(`pendenzen.component.ts; ${context}();`, error);
    }

    /** Terminal for `catchError` pipelines: recovers by completing without a value. */
    private recover(): Observable<never> {
        return EMPTY;
    }
}
